/**
 * 폐기물 시험일지 → 시험성적서/결과통보서 파생.
 * 삼성분·원소분석은 서버 표준 추출 결과를 전개하고,
 * 물리적조성은 physComposition(시료별)에서 조성행을 합성한다.
 */
import api from '@/api'
import { flattenPhysCompositionForReport } from '@/utils/physComposition'

export const DISPOSAL_REPORT_GROUP_IDS = [
	'DISPOSAL_TEST_116', // 삼성분
	'DISPOSAL_TEST_124', // 원소분석
	'DISPOSAL_TEST_134', // 물리적조성(소각)
	'DISPOSAL_TEST_145', // 물리적조성(매립)
	'DISPOSAL_TEST_156', // 물리적조성(공사현장)
	// DISPOSAL_TEST_131(Cl,S) 는 그룹 셀 없이 Cl/S만 개별 표시
]

const ELEMENT_S_ID = 'DISPOSAL_TEST_128'
const ELEMENT_CL_ID = 'DISPOSAL_TEST_130'
const ELEMENT_PARENT_ID = 'DISPOSAL_TEST_124'
const ELEMENT_CHILD_IDS = [
	'DISPOSAL_TEST_125',
	'DISPOSAL_TEST_126',
	'DISPOSAL_TEST_127',
	ELEMENT_S_ID,
	'DISPOSAL_TEST_129',
	ELEMENT_CL_ID,
]
const CLS_CL_ID = 'DISPOSAL_TEST_132'
const CLS_S_ID = 'DISPOSAL_TEST_133'
const CLS_PARENT_ID = 'DISPOSAL_TEST_131'
const CLS_CHILD_IDS = [CLS_CL_ID, CLS_S_ID]

const DISPOSAL_STANDARD_PARENTS = [
	{
		parentId: 'DISPOSAL_TEST_116',
		childIds: ['DISPOSAL_TEST_117', 'DISPOSAL_TEST_118', 'DISPOSAL_TEST_119'],
		withSubtotal: true,
	},
	{
		// Cl,S: 시료등록에서 Cl/S를 개별 선택. 성적서·결과통보서도 개별 행(그룹 셀 없음).
		parentId: 'DISPOSAL_TEST_131',
		childIds: CLS_CHILD_IDS,
		withSubtotal: false,
	},
	{
		parentId: 'DISPOSAL_TEST_124',
		childIds: ELEMENT_CHILD_IDS,
		withSubtotal: false,
	},
]

export const DISPOSAL_PHYS_COMP_IDS = [
	'DISPOSAL_TEST_134',
	'DISPOSAL_TEST_145',
	'DISPOSAL_TEST_156',
]

/**
 * 이중표 합산 부모 — 결과확인창에서 그룹이어도 합산 행 노출.
 * 085만: 시료등록 부모 선택. 055(할로겐족)는 하위 개별 선택 → 제외.
 */
export const DISPOSAL_DUAL_AGGREGATE_IDS = [
	'DISPOSAL_TEST_085', // 할로겐화유기물질
]

const labelOf = (cat) => [
	cat?.name,
	cat?.init_name,
	cat?.lab_name,
	cat?.search_name,
].filter(Boolean)

const categoryMatchesLabel = (cat, label) => {
	const target = String(label ?? '').trim()
	if (!target || !cat) return false
	return labelOf(cat).some(v => String(v).trim() === target)
}

/**
 * 여러 시료 categories 를 등장 순(first-seen)으로 합친다.
 * 동명 라벨(용출 '비소'(020) vs 함량 '비소'(033) 등)이 한 시료 안에서 2번 선택되면
 * 두 번 모두 유지해야 expand 의 findByLabel 이 미사용 testId 로 각각 매칭한다.
 * (단순 dedup 시 함량 항목이 매칭 라벨을 잃고 leftover 로 하단에 밀려 화면·성적서 순서가 어긋남)
 * 교차 시료 과다 누적은 막기 위해, 라벨별 "한 시료 최대 등장 횟수"까지만 유지한다.
 */
export function mergeDisposalSelectionLabels(sampleList) {
	const out = []
	const emitted = new Map() // label -> 지금까지 out 에 넣은 누적 개수
	;(sampleList || []).forEach(sample => {
		let cats = sample?.categories
		if (typeof cats === 'string') {
			try { cats = JSON.parse(cats) } catch { cats = [] }
		}
		if (!Array.isArray(cats)) return
		const local = new Map() // 이 시료 내 등장 순번
		cats.forEach(label => {
			const key = String(label ?? '').trim()
			if (!key) return
			const nth = (local.get(key) || 0) + 1
			local.set(key, nth)
			if (nth > (emitted.get(key) || 0)) {
				out.push(key)
				emitted.set(key, nth)
			}
		})
	})
	return out
}

/**
 * 수질과 동일: sample.categories 선택 순서로 DISPOSAL category 행을 전개한다.
 * - _hideInReport 부모는 숨기고, 선택·노출 대상 자식만 부모 슬롯/개별 슬롯에서 삽입
 * - 동명 라벨(Cl 130 vs 132 등)은 showIds·미사용 항목을 우선 매칭
 *
 * @param {object[]} categoryList
 * @param {{ categories?: string[], showIds?: Iterable<string>, enforceDocumentHide?: boolean }} opts
 */
export function expandDisposalCategoryBySelectionOrder(categoryList, opts = {}) {
	const fullList = Array.isArray(categoryList) ? categoryList : []
	let categories = opts.categories
	if (typeof categories === 'string') {
		try { categories = JSON.parse(categories) } catch { categories = [] }
	}
	if (!Array.isArray(categories)) categories = []
	const showSet = new Set(
		opts.showIds
			? [...opts.showIds].map(String)
			: fullList.map(c => c?.testId).filter(Boolean).map(String),
	)
	const enforceDocumentHide = opts.enforceDocumentHide !== false
	const used = new Set()
	const expanded = []

	// hideGroup·이중표 합산 부모: type==='group' 이어도 합산 행으로 노출 (강등 전 canPush 거부 금지)
	const isHideGroupAggregate = (cat) =>
		cat?.hide_type === 'hideGroup' && cat.testId !== 'SOIL_TEST_016'
	const isAggregateParent = (cat) =>
		isHideGroupAggregate(cat) || DISPOSAL_DUAL_AGGREGATE_IDS.includes(cat?.testId)

	const canPush = (cat) => {
		if (!cat?.testId || used.has(cat.testId) || cat._hideInReport) return false
		if (cat.type === 'group' && !isAggregateParent(cat)) return false
		// 합성 파생행(__ROW_/__TOTAL): showIds 에 본인 없음 → 부모 선택이면 노출.
		// 원소분석·삼성분 등 실마스터 자식은 기존처럼 본인 showIds 필요.
		const tid = String(cat.testId)
		const shownDirect = showSet.has(tid)
		const isSyntheticDerived =
			!!cat._derivedFrom && (tid.includes('__ROW_') || tid.includes('__TOTAL'))
		const shownViaParent =
			isSyntheticDerived && showSet.has(String(cat._derivedFrom))
		if (!shownDirect && !shownViaParent) return false
		if (
			enforceDocumentHide &&
			!cat._derivedFrom &&
			cat.is_document_hide !== 0 &&
			cat.is_document_hide != null
		) {
			return false
		}
		return true
	}

	const pushCat = (cat) => {
		if (!canPush(cat)) return
		if (isAggregateParent(cat)) {
			cat.type = 'test'
		}
		expanded.push(cat)
		used.add(cat.testId)
	}

	const findByLabel = (label) => {
		const matches = fullList.filter(cat => categoryMatchesLabel(cat, label))
		if (!matches.length) return null
		const unused = matches.filter(c => c?.testId && !used.has(c.testId))
		const pool = unused.length ? unused : matches
		const shown = pool.find(c => showSet.has(String(c.testId)))
		if (shown) return shown
		// 부모(_hideInReport)는 showIds에 없어도 전개 트리거로 사용
		const parent = pool.find(c => c._hideInReport)
		return parent || pool[0]
	}

	const orderDerivedChildren = (parentId) => {
		const children = fullList.filter(
			c => c._derivedFrom === parentId && !c._hideInReport,
		)
		const ordered = []
		const remaining = [...children]
		categories.forEach(label => {
			const idx = remaining.findIndex(c => categoryMatchesLabel(c, label))
			if (idx < 0) return
			ordered.push(remaining.splice(idx, 1)[0])
		})
		return ordered.concat(remaining)
	}

	const expandParent = (parentId) => {
		orderDerivedChildren(parentId).forEach(pushCat)
	}

	categories.forEach(label => {
		const found = findByLabel(label)
		if (!found) return
		if (found._hideInReport) {
			used.add(found.testId)
			expandParent(found.testId)
			return
		}
		pushCat(found)
	})

	// 선택 라벨 매칭 실패·누락분 보완 (마스터 list 순, 단 선택 자식은 본인 showIds)
	fullList.forEach(cat => {
		if (!cat?.testId || used.has(cat.testId)) return
		if (cat._hideInReport) {
			if (showSet.has(String(cat.testId))) expandParent(cat.testId)
			return
		}
		if (cat._derivedFrom) {
			pushCat(cat)
			return
		}
		pushCat(cat)
	})

	return expanded
}

const parseJournalResult = (raw) => {
	if (raw == null) return null
	if (typeof raw === 'string') {
		try {
			return JSON.parse(raw)
		} catch {
			return null
		}
	}
	return raw
}

const toFixed2 = (v) => {
	const n = Number(v)
	return v === '' || v == null || isNaN(n) ? '-' : n.toFixed(2)
}

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key)

/** 하위 선택값이 있으면 이를 우선하고, 구데이터(자식 키 없음)만 부모 isTest로 폴백한다. */
function isDisposalChildSelected(sample, childId, parentId, siblingIds) {
	const testResult = sample?.testResult || {}
	if (hasOwn(testResult, childId)) return testResult[childId]?.isTest === true
	if (siblingIds.some(id => hasOwn(testResult, id))) return false
	return testResult[parentId]?.isTest === true
}

/**
 * Cl/S 우선순위 판정만 수행 (샘플 변이 없음).
 * Cl·S를 연속 적용할 때 한쪽의 _derivedFromParent 기록이 다른 쪽 부모 폴백을 깨지 않게 한다.
 */
function planDisposalElementOverClsPriority(sampleList, { elementChildId, clsChildId }) {
	const selectedClsSamples = (sampleList || []).filter(sample =>
		isDisposalChildSelected(sample, clsChildId, CLS_PARENT_ID, CLS_CHILD_IDS),
	)
	const hiddenSampleNos = new Set(
		selectedClsSamples
			.filter(sample =>
				isDisposalChildSelected(sample, elementChildId, ELEMENT_PARENT_ID, ELEMENT_CHILD_IDS),
			)
			.map(sample => String(sample.sampleNo ?? '')),
	)
	const suppressRow =
		selectedClsSamples.length > 0 &&
		selectedClsSamples.every(sample => hiddenSampleNos.has(String(sample.sampleNo ?? '')))
	return { selectedClsSamples, hiddenSampleNos, suppressRow, clsChildId }
}

/** 판정 결과를 문서 사본에 제자리 반영 (DB 변경 없음). */
function applyDisposalElementOverClsPlan(category, resultList, plan, hiddenFlag) {
	const { selectedClsSamples, hiddenSampleNos, suppressRow, clsChildId } = plan

	selectedClsSamples.forEach(sample => {
		if (!sample.testResult) sample.testResult = {}
		if (!hasOwn(sample.testResult, clsChildId)) {
			sample.testResult[clsChildId] = { isTest: true, _derivedFromParent: true }
		}
	})

	if (hiddenSampleNos.size === 0) {
		return { hiddenSampleNos, suppressRow: false }
	}

	selectedClsSamples.forEach(sample => {
		const sampleNo = String(sample.sampleNo ?? '')
		if (!hiddenSampleNos.has(sampleNo)) return
		if (!sample.testResult) sample.testResult = {}
		sample.testResult[clsChildId] = {
			...(sample.testResult[clsChildId] || {}),
			isTest: false,
			[hiddenFlag]: true,
		}
	})

	const resultCls = (resultList || []).find(row => row.testId === clsChildId)
	if (resultCls?.sampleList) {
		resultCls.sampleList = resultCls.sampleList.map(sample =>
			hiddenSampleNos.has(String(sample.no ?? ''))
				? { ...sample, value: '-' }
				: sample,
		)
	}

	if (suppressRow) {
		if (Array.isArray(category?.list)) {
			category.list = category.list.filter(item => item.testId !== clsChildId)
		}
		const resultIndex = (resultList || []).findIndex(row => row.testId === clsChildId)
		if (resultIndex >= 0) resultList.splice(resultIndex, 1)
	}

	return { hiddenSampleNos, suppressRow }
}

/**
 * 원소분석 Cl(130) > Cl,S Cl(132), 원소분석 S(128) > Cl,S S(133).
 */
export function applyDisposalClPriority(category, resultList, sampleList) {
	const clPlan = planDisposalElementOverClsPriority(sampleList, {
		elementChildId: ELEMENT_CL_ID,
		clsChildId: CLS_CL_ID,
	})
	const sPlan = planDisposalElementOverClsPriority(sampleList, {
		elementChildId: ELEMENT_S_ID,
		clsChildId: CLS_S_ID,
	})
	const cl = applyDisposalElementOverClsPlan(
		category, resultList, clPlan, '_hiddenByClPriority',
	)
	const s = applyDisposalElementOverClsPlan(
		category, resultList, sPlan, '_hiddenBySPriority',
	)
	return {
		hiddenSampleNos: cl.hiddenSampleNos,
		suppress132Row: cl.suppressRow,
		hiddenSSampleNos: s.hiddenSampleNos,
		suppress133Row: s.suppressRow,
	}
}

/**
 * category.list / resultList 를 성적서·결과통보서 커밋 전에 제자리 확장한다.
 * @param {{ includeSubtotal?: boolean }} options
 *   - includeSubtotal: 삼성분 소계(XXX__TOTAL) 생성 여부.
 *     시험성적서 true(기본), 결과확인창(ModalResult)/결과통보서 false.
 */
export async function deriveDisposalJournalResults(
	projectId,
	category,
	resultList,
	sampleList,
	{ includeSubtotal = true } = {},
) {
	const catList = category?.list || []
	const registeredIds = new Set()
	;(sampleList || []).forEach((s) => {
		Object.keys(s?.testResult || {}).forEach((k) => {
			if (s.testResult[k]?.isTest) registeredIds.add(k)
		})
	})
	const samplesOf = (testId) =>
		(sampleList || []).filter((s) => s?.testResult?.[testId]?.isTest).map((s) => s.sampleNo)

	const upsertResult = (testId, unit, samples) => {
		const entry = resultList.find((r) => r.testId === testId)
		if (entry) {
			entry.sampleList = samples
			if (!entry.testResultUnit) entry.testResultUnit = unit
		} else {
			resultList.push({
				testId,
				testResultUnit: unit,
				testMethod: null,
				testMachine: null,
				sampleList: samples,
			})
		}
	}
	const catOf = (testId) => catList.find((c) => c.testId === testId)

	for (const { parentId, childIds, withSubtotal } of DISPOSAL_STANDARD_PARENTS) {
		// 삼성분(116)·Cl,S(131): 시료등록에서 부모 선택 → registeredIds.has(parentId)
		// 원소분석(124): 치환성양이온형 — 하위만 개별 선택 → 자식 isTest로도 진입
		const childSelected = childIds.some((id) => registeredIds.has(id))
		if (!registeredIds.has(parentId) && !childSelected) continue
		const parent = catOf(parentId)
		if (parent) parent._hideInReport = true
		childIds.forEach((id) => {
			const child = catOf(id)
			if (child) child._derivedFrom = parentId
		})
		// 성적서만 소계(수분+회분+가연분). 결과확인창·시험일지는 소계 미표시.
		if (!withSubtotal || !includeSubtotal || !parent) continue
		const subtotal = new Map() // no → sum
		const sampleNos = new Set()
		childIds.forEach((id) => {
			const entry = resultList.find((r) => r.testId === id)
			;(entry?.sampleList || []).forEach(({ no, value }) => {
				sampleNos.add(no)
				const n = Number(value)
				if (!isNaN(n)) subtotal.set(no, (subtotal.get(no) || 0) + n)
			})
		})
		// 자식 값이 없어도 시료번호는 부모/자식 등록 기준으로 확보
		samplesOf(parentId).forEach((no) => sampleNos.add(no))
		const totalId = `${parentId}__TOTAL`
		if (!catOf(totalId)) {
			catList.push({
				...parent,
				testId: totalId,
				name: '소계',
				lab_name: '소계',
				search_name: '소계',
				type: 'test',
				hide_parent_id: parentId,
				parent_id: null,
				// 부모(삼성분) is_document_hide 상속 시 성적서 필터에서 탈락하는 것 방지
				is_document_hide: 0,
				is_report_hide: 0,
				doc_order_no: Number(parent.doc_order_no || 0) + 0.9,
				_derivedFrom: parentId,
				_hideInReport: false,
			})
		} else {
			const existing = catOf(totalId)
			existing.name = '소계'
			existing.lab_name = '소계'
			existing.search_name = '소계'
			existing.is_document_hide = 0
			existing.is_report_hide = 0
		}
		upsertResult(
			totalId,
			'%',
			// 성적서 샘플: 100.0 (소수 1자리). 합산 불가 시료는 '-'
			[...sampleNos].map((no) => ({
				no,
				value: subtotal.has(no) ? subtotal.get(no).toFixed(1) : '-',
			})),
		)
	}

	for (const parentId of DISPOSAL_PHYS_COMP_IDS) {
		if (!registeredIds.has(parentId)) continue
		const journal = await api.project.getResult(projectId, parentId).catch(() => null)
		const r = parseJournalResult(journal?.result)
		const sampleNos = samplesOf(parentId)
		if (!sampleNos.length) continue
		const parent = catOf(parentId)
		if (!parent) continue

		// 사용자가 선택한 조성항목 행을 그대로 펼침 (고정행 강제 병합 없음)
		// 공사현장(156): group(가연성/비가연성) → 성적서 subGroupName
		const flat = flattenPhysCompositionForReport(r?.physComposition, sampleNos, { isConst: false })
		if (!flat.length) continue

		parent._hideInReport = true
		flat.forEach((row, i) => {
			const rowId = `${parentId}__ROW_${i}`
			const subGroupName =
				!row.isSoilRow && row.group && parentId === 'DISPOSAL_TEST_156'
					? row.group
					: null
			if (!catOf(rowId)) {
				catList.push({
					...parent,
					testId: rowId,
					name: row.name,
					lab_name: row.name,
					search_name: row.name, // DISPOSAL 표시는 search_name 우선 — 부모 search_name 잔존 방지
					type: 'test',
					hide_parent_id: parentId,
					parent_id: null,
					subGroupName,
					doc_order_no: Number(parent.doc_order_no || 0) + (i + 1) * 0.01,
					_derivedFrom: parentId,
					_hideInReport: false,
				})
			} else {
				const existing = catOf(rowId)
				existing.name = row.name
				existing.lab_name = row.name
				existing.search_name = row.name
				existing.subGroupName = subGroupName
			}
			upsertResult(
				rowId,
				'%',
				sampleNos.map((no) => ({
					no,
					value: toFixed2(row.values.get(String(no))),
				})),
			)
		})
	}

	applyDisposalClPriority(category, resultList, sampleList)
}
